<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const CONTACTS_PER_PAGE = 100;

const WORLD_REGIONS = [
  {
    key: 'north_america',
    labelKey: 'NORTH_AMERICA',
    markerClass: 'left-[20%] top-[35%]',
    countries: [
      'canada',
      'ca',
      'united states',
      'united states of america',
      'usa',
      'us',
      'mexico',
      'mx',
    ],
  },
  {
    key: 'south_america',
    labelKey: 'SOUTH_AMERICA',
    markerClass: 'left-[32%] top-[68%]',
    countries: [
      'argentina',
      'ar',
      'brazil',
      'br',
      'chile',
      'cl',
      'colombia',
      'co',
      'peru',
      'pe',
    ],
  },
  {
    key: 'europe',
    labelKey: 'EUROPE',
    markerClass: 'left-[49%] top-[32%]',
    countries: [
      'france',
      'fr',
      'germany',
      'de',
      'italy',
      'it',
      'netherlands',
      'nl',
      'spain',
      'es',
      'united kingdom',
      'uk',
      'gb',
    ],
  },
  {
    key: 'africa',
    labelKey: 'AFRICA',
    markerClass: 'left-[51%] top-[58%]',
    countries: [
      'egypt',
      'eg',
      'kenya',
      'ke',
      'morocco',
      'ma',
      'nigeria',
      'ng',
      'south africa',
      'za',
    ],
  },
  {
    key: 'asia',
    labelKey: 'ASIA',
    markerClass: 'left-[68%] top-[43%]',
    countries: [
      'china',
      'cn',
      'hong kong',
      'hk',
      'india',
      'in',
      'indonesia',
      'id',
      'japan',
      'jp',
      'singapore',
      'sg',
      'south korea',
      'kr',
    ],
  },
  {
    key: 'oceania',
    labelKey: 'OCEANIA',
    markerClass: 'left-[81%] top-[70%]',
    countries: ['australia', 'au', 'new zealand', 'nz'],
  },
  {
    key: 'unknown',
    labelKey: 'UNKNOWN',
    markerClass: 'left-[15%] top-[78%]',
    countries: [],
  },
];

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const visitors = ref([]);
const isFetching = ref(false);
const isSending = ref(false);
const selectedRegionKey = ref('');
const selectedContact = ref(null);
const messageDraft = ref('');
const selectedProductId = useMapGetter('products/getSelectedProductId');

const normalize = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const regionLabel = labelKey => {
  switch (labelKey) {
    case 'NORTH_AMERICA':
      return t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.NORTH_AMERICA');
    case 'SOUTH_AMERICA':
      return t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.SOUTH_AMERICA');
    case 'EUROPE':
      return t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.EUROPE');
    case 'AFRICA':
      return t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.AFRICA');
    case 'ASIA':
      return t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.ASIA');
    case 'OCEANIA':
      return t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.OCEANIA');
    default:
      return t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.UNKNOWN');
  }
};

const unknownRegion = () =>
  WORLD_REGIONS.find(region => region.key === 'unknown');

const getLocation = contact => {
  const attrs = contact.additional_attributes || {};
  return {
    city: attrs.city || contact.location || '',
    country: attrs.country || contact.country_code || '',
  };
};

const getRegion = contact => {
  const { country } = getLocation(contact);
  const normalizedCountry = normalize(country);
  if (!normalizedCountry) return unknownRegion();

  return (
    WORLD_REGIONS.find(region =>
      region.countries.some(countryAlias => normalizedCountry === countryAlias)
    ) || unknownRegion()
  );
};

const contactDisplayName = contact =>
  contact.name ||
  contact.email ||
  contact.phone_number ||
  t('CONTACTS_LAYOUT.VISITOR_MAP.UNKNOWN_VISITOR');

const contactLocationLabel = contact => {
  const { city, country } = getLocation(contact);
  if (city && country) return `${city}, ${country}`;
  return city || country || regionLabel('UNKNOWN');
};

const webWidgetInboxes = contact =>
  (contact.contact_inboxes || []).filter(
    item => item.inbox?.channel_type === 'Channel::WebWidget'
  );

const productFilteredVisitors = computed(() => {
  if (!selectedProductId.value) return visitors.value;

  const productId = Number(selectedProductId.value);
  return visitors.value.filter(contact =>
    webWidgetInboxes(contact).some(item => item.inbox?.product_id === productId)
  );
});

const visibleWebWidgetInboxes = contact => {
  const inboxes = webWidgetInboxes(contact);
  if (!selectedProductId.value) return inboxes;

  const productId = Number(selectedProductId.value);
  return inboxes.filter(item => item.inbox?.product_id === productId);
};

const regionGroups = computed(() => {
  const groups = new Map();

  productFilteredVisitors.value.forEach(contact => {
    const region = getRegion(contact);
    const existing = groups.get(region.key) || {
      key: region.key,
      label: regionLabel(region.labelKey),
      markerClass: region.markerClass,
      contacts: [],
    };

    existing.contacts.push(contact);
    groups.set(region.key, existing);
  });

  return Array.from(groups.values())
    .map(group => ({ ...group, count: group.contacts.length }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
});

const selectedRegion = computed(() => {
  return (
    regionGroups.value.find(group => group.key === selectedRegionKey.value) ||
    regionGroups.value[0]
  );
});

const totalVisitors = computed(() => productFilteredVisitors.value.length);
const totalRegions = computed(() => regionGroups.value.length);
const selectedContactInbox = computed(
  () => visibleWebWidgetInboxes(selectedContact.value || {})[0]
);
const canSendMessage = computed(
  () =>
    selectedContact.value &&
    selectedContactInbox.value &&
    messageDraft.value.trim().length > 0
);

const syncSelectionToFirstVisitor = () => {
  const firstRegion = regionGroups.value[0];
  selectedRegionKey.value = firstRegion?.key || '';
  selectedContact.value = firstRegion?.contacts[0] || null;
};

const fetchActiveVisitors = async () => {
  isFetching.value = true;
  selectedContact.value = null;

  try {
    const {
      data: { payload, meta },
    } = await ContactAPI.active(1, '-last_activity_at', {
      includeContactInboxes: true,
      perPage: CONTACTS_PER_PAGE,
    });
    const totalCount = meta.count || payload.length;
    const totalPages = Math.ceil(totalCount / CONTACTS_PER_PAGE);
    const remainingPages = Array.from(
      { length: Math.max(totalPages - 1, 0) },
      (_item, index) => index + 2
    );
    const remainingResponses = await Promise.all(
      remainingPages.map(page =>
        ContactAPI.active(page, '-last_activity_at', {
          includeContactInboxes: true,
          perPage: CONTACTS_PER_PAGE,
        })
      )
    );
    const nextVisitors = [
      ...payload,
      ...remainingResponses.flatMap(response => response.data.payload),
    ];

    visitors.value = nextVisitors;
    syncSelectionToFirstVisitor();
  } catch {
    useAlert(t('CONTACTS_LAYOUT.VISITOR_MAP.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const selectRegion = region => {
  selectedRegionKey.value = region.key;
  selectedContact.value = region.contacts[0] || null;
};

const selectContact = contact => {
  selectedContact.value = contact;
};

const openConversation = conversation => {
  router.push(
    frontendURL(
      conversationUrl({
        accountId: route.params.accountId,
        id: conversation.id,
      })
    )
  );
};

const sendMessage = async () => {
  if (!canSendMessage.value || isSending.value) return;

  isSending.value = true;
  try {
    const payload = await store.dispatch('contacts/proactiveMessage', {
      contactId: selectedContact.value.id,
      contactInboxId: selectedContactInbox.value.id,
      content: messageDraft.value.trim(),
    });

    useAlert(t('CONTACTS_LAYOUT.VISITOR_MAP.SEND_SUCCESS'));
    messageDraft.value = '';
    openConversation(payload.conversation);
  } catch (error) {
    useAlert(error.message || t('CONTACTS_LAYOUT.VISITOR_MAP.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

watch(selectedProductId, syncSelectionToFirstVisitor);

onMounted(() => {
  fetchActiveVisitors();
});
</script>

<template>
  <section class="flex flex-col h-full overflow-hidden bg-n-surface-1">
    <header
      class="flex flex-col gap-3 px-6 py-4 border-b border-n-weak md:flex-row md:items-center md:justify-between"
    >
      <div class="min-w-0">
        <h1 class="text-xl font-medium truncate text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.VISITOR_MAP.TITLE') }}
        </h1>
        <div
          class="flex flex-wrap items-center gap-3 mt-1 text-sm text-n-slate-11"
        >
          <span>
            {{
              t('CONTACTS_LAYOUT.VISITOR_MAP.ONLINE_COUNT', {
                count: totalVisitors,
              })
            }}
          </span>
          <span>
            {{
              t('CONTACTS_LAYOUT.VISITOR_MAP.REGION_COUNT', {
                count: totalRegions,
              })
            }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <Button
          faded
          slate
          sm
          icon="i-lucide-refresh-cw"
          :label="t('CONTACTS_LAYOUT.VISITOR_MAP.REFRESH')"
          :is-loading="isFetching"
          @click="fetchActiveVisitors"
        />
      </div>
    </header>

    <main
      class="grid flex-1 min-h-0 gap-4 p-4 overflow-hidden lg:grid-cols-[minmax(0,1fr)_24rem]"
    >
      <div
        class="relative min-h-0 overflow-hidden rounded-lg border border-n-weak bg-n-surface-2"
      >
        <div
          v-if="isFetching"
          class="flex items-center justify-center h-full text-n-slate-11"
        >
          <Spinner />
        </div>

        <div
          v-else-if="!totalVisitors"
          class="flex items-center justify-center h-full px-6 text-sm text-center text-n-slate-11"
        >
          {{ t('CONTACTS_LAYOUT.VISITOR_MAP.EMPTY') }}
        </div>

        <div v-else class="relative h-full min-h-[30rem] overflow-hidden">
          <svg
            viewBox="0 0 960 520"
            aria-hidden="true"
            class="absolute inset-0 w-full h-full text-n-slate-5 dark:text-n-slate-6"
          >
            <path
              class="fill-current opacity-60"
              d="M112 174l58-35 74 10 35 39-26 47 22 40-42 35-70-18-59-49z"
            />
            <path
              class="fill-current opacity-60"
              d="M265 310l48 20 37 65-18 72-47-8-35-76z"
            />
            <path
              class="fill-current opacity-70"
              d="M430 150l68-24 69 19 25 49-42 37-84-9-43-33z"
            />
            <path
              class="fill-current opacity-60"
              d="M470 260l75-28 71 55-12 96-58 55-70-45-30-79z"
            />
            <path
              class="fill-current opacity-70"
              d="M594 166l126-45 105 28 47 77-38 82-99 23-88-44-67-61z"
            />
            <path
              class="fill-current opacity-60"
              d="M752 348l82 25 42 63-56 29-81-20-25-55z"
            />
          </svg>

          <button
            v-for="region in regionGroups"
            :key="region.key"
            type="button"
            class="absolute flex items-center justify-center -translate-x-1/2 -translate-y-1/2 rounded-full outline-none group"
            :class="region.markerClass"
            @click="selectRegion(region)"
          >
            <span
              class="absolute rounded-full size-12 bg-n-teal-9/20 group-hover:bg-n-teal-9/30"
              :class="{ 'animate-ping': selectedRegion?.key === region.key }"
            />
            <span
              class="relative flex items-center justify-center font-medium text-white rounded-full shadow-sm size-11 bg-n-teal-9 ring-4 ring-n-surface-1"
              :class="{ 'bg-n-brand': selectedRegion?.key === region.key }"
            >
              {{ region.count }}
            </span>
            <span class="sr-only">
              {{
                t('CONTACTS_LAYOUT.VISITOR_MAP.REGION_MARKER', {
                  region: region.label,
                  count: region.count,
                })
              }}
            </span>
          </button>
        </div>
      </div>

      <aside
        class="flex flex-col min-h-0 rounded-lg border border-n-weak bg-n-surface-1"
      >
        <div class="p-4 border-b border-n-weak">
          <h2 class="text-base font-medium truncate text-n-slate-12">
            {{
              selectedRegion?.label ||
              t('CONTACTS_LAYOUT.VISITOR_MAP.REGIONS.UNKNOWN')
            }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{
              t('CONTACTS_LAYOUT.VISITOR_MAP.SELECTED_REGION_COUNT', {
                count: selectedRegion?.count || 0,
              })
            }}
          </p>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto">
          <button
            v-for="contact in selectedRegion?.contacts || []"
            :key="contact.id"
            type="button"
            class="flex w-full gap-3 px-4 py-3 text-left border-b outline-none border-n-weak hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': selectedContact?.id === contact.id }"
            @click="selectContact(contact)"
          >
            <Avatar
              :name="contactDisplayName(contact)"
              :src="contact.thumbnail"
              :size="36"
              status="online"
              rounded-full
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium truncate text-n-slate-12">
                {{ contactDisplayName(contact) }}
              </span>
              <span class="block text-xs truncate text-n-slate-11">
                {{ contactLocationLabel(contact) }}
              </span>
              <span
                v-if="visibleWebWidgetInboxes(contact).length"
                class="block text-xs truncate text-n-slate-10"
              >
                {{
                  visibleWebWidgetInboxes(contact)
                    .map(item => item.inbox.name)
                    .join(', ')
                }}
              </span>
            </span>
          </button>
        </div>

        <div class="p-4 border-t border-n-weak">
          <div v-if="selectedContact" class="flex flex-col gap-3">
            <div class="min-w-0 text-sm">
              <span class="block font-medium truncate text-n-slate-12">
                {{ contactDisplayName(selectedContact) }}
              </span>
              <span class="block truncate text-n-slate-11">
                {{ contactLocationLabel(selectedContact) }}
              </span>
            </div>
            <TextArea
              v-model="messageDraft"
              auto-height
              :label="t('CONTACTS_LAYOUT.VISITOR_MAP.MESSAGE_LABEL')"
              :placeholder="
                t('CONTACTS_LAYOUT.VISITOR_MAP.MESSAGE_PLACEHOLDER')
              "
              :max-length="1000"
              :disabled="isSending"
            />
            <Button
              solid
              blue
              icon="i-lucide-send"
              :label="t('CONTACTS_LAYOUT.VISITOR_MAP.SEND')"
              :disabled="!canSendMessage"
              :is-loading="isSending"
              @click="sendMessage"
            />
          </div>
          <p v-else class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.VISITOR_MAP.NO_SELECTION') }}
          </p>
        </div>
      </aside>
    </main>
  </section>
</template>
